/**
 * Timezone-safe helpers for "date-only" values (DOB, effective date, marriage
 * date, graduation date, ...).
 *
 * The bug these solve: `new Date('1990-05-10')` is parsed by JS as UTC
 * midnight. Reading it back with the local getters (`getDate()`, or anything
 * that renders in local time, such as a datepicker) gives 1990-05-09 in any
 * timezone west of UTC (all of Canada/USA). Going the other way,
 * `date.toISOString()` on a Date the picker created at LOCAL midnight rolls
 * back to the previous day for the same timezones. Doing both in one round
 * trip is what produces the occasional two-day shift.
 *
 * Rule for the whole app: a date-only value is always represented in memory as
 * a Date at LOCAL midnight, and always serialised with the local getters.
 * Never use `toISOString()` on one.
 */

// 2024-05-10, optionally carrying a midnight time part (with or without Z).
const ISO_DATE_ONLY = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ]0{2}:0{2}(?::0{2}(?:\.0+)?)?Z?)?$/;
// 05-10-2024 or 05/10/2024
const US_DATE_ONLY = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/;
// May-10-2024 (the format every picker in the portal displays)
const MMM_DATE_ONLY = /^([A-Za-z]{3,})[-\/ ](\d{1,2})[-\/ ](\d{4})$/;
// 10-May-2024 (legacy display format, still found in stored data)
const DATE_MMM_ONLY = /^(\d{1,2})[-\/ ]([A-Za-z]{3,})[-\/ ](\d{4})$/;
// 12122000 - what people actually type when they don't use the calendar
const DIGITS_ONLY = /^(\d{8})$/;
// Any ISO string carrying a real (non-midnight) time part.
const ISO_WITH_TIME = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ]/;

/** Builds a Date at local midnight; no timezone shifting can occur. */
export function localDate(year: number, month1Based: number, day: number): Date {
  const d = new Date(year, month1Based - 1, day, 0, 0, 0, 0);
  return isNaN(d.getTime()) ? null as any : d;
}

/**
 * Parses any date-only representation into a Date at LOCAL midnight.
 * Accepts Date, 'YYYY-MM-DD', 'YYYY-MM-DDT00:00:00.000Z', 'MM-DD-YYYY',
 * 'MM/DD/YYYY' and 'DD-MMM-YYYY'. Returns null for empty/invalid input.
 */
export function parseDateOnly(value: any): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime())
      ? null
      : localDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : localDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  // 'YYYY-MM-DD' (and the UTC-midnight form the API sends): read the calendar
  // parts straight out of the string so no timezone conversion is involved.
  const iso = ISO_DATE_ONLY.exec(raw);
  if (iso) {
    return localDate(+iso[1], +iso[2], +iso[3]);
  }

  const us = US_DATE_ONLY.exec(raw);
  if (us) {
    return localDate(+us[3], +us[1], +us[2]);
  }

  const mmm = MMM_DATE_ONLY.exec(raw);
  if (mmm) {
    const mi = monthIndex(mmm[1]);
    return mi < 0 ? null : localDate(+mmm[3], mi + 1, +mmm[2]);
  }

  const dmy = DATE_MMM_ONLY.exec(raw);
  if (dmy) {
    const mi = monthIndex(dmy[2]);
    return mi < 0 ? null : localDate(+dmy[3], mi + 1, +dmy[1]);
  }

  const digits = DIGITS_ONLY.exec(raw);
  if (digits) {
    return parseEightDigits(digits[1]);
  }

  // A real timestamp: let the engine parse it, then keep only the calendar day
  // as the user's own clock sees it.
  const withTime = ISO_WITH_TIME.exec(raw);
  if (withTime) {
    const d = new Date(raw);
    if (isNaN(d.getTime())) {
      return null;
    }
    return localDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
  }

  // 'DD-MMM-YYYY' and any other format the engine understands.
  const parsed = new Date(raw.replace(/-/g, ' '));
  if (isNaN(parsed.getTime())) {
    const fallback = new Date(raw);
    if (isNaN(fallback.getTime())) {
      return null;
    }
    return localDate(fallback.getFullYear(), fallback.getMonth() + 1, fallback.getDate());
  }
  return localDate(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
}

/**
 * Fixed English month abbreviations. Deliberately not locale-derived: the
 * datepicker renders with ngx-bootstrap's 'en' locale, so the text next to it
 * must not change with the viewer's region either.
 */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Month index (0-11) for a month name or abbreviation; -1 when unknown. */
function monthIndex(name: string): number {
  const key = name.slice(0, 3).toLowerCase();
  return MONTHS_SHORT.findIndex(m => m.toLowerCase() === key);
}

/** True when y/m/d name a real calendar day (rejects 02-31-2000 and friends). */
function isRealDate(y: number, m: number, d: number): boolean {
  if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1 || d > 31) {
    return false;
  }
  const probe = new Date(y, m - 1, d);
  return probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d;
}

/**
 * 8 bare digits, tried in the order that resolves the ambiguity the way the
 * portal always has: MMDDYYYY first, since that is what every picker displays,
 * then YYYYMMDD, then DDMMYYYY. Only the genuinely ambiguous case (both halves
 * <= 12, e.g. 12122000) is decided by that order; 20001212 and 13122000 each
 * match exactly one reading.
 */
function parseEightDigits(digits: string): Date | null {
  const candidates: [number, number, number][] = [
    [+digits.slice(4), +digits.slice(0, 2), +digits.slice(2, 4)],  // MMDDYYYY
    [+digits.slice(0, 4), +digits.slice(4, 6), +digits.slice(6)],  // YYYYMMDD
    [+digits.slice(4), +digits.slice(2, 4), +digits.slice(0, 2)],  // DDMMYYYY
  ];
  for (const [y, m, d] of candidates) {
    if (isRealDate(y, m, d)) {
      return localDate(y, m, d);
    }
  }
  return null;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' for the API. Replaces `toISOString().split('T')[0]`. */
export function toApiDate(value: any): string | null {
  const d = parseDateOnly(value);
  if (!d) {
    return null;
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * 'MMM-DD-YYYY' (e.g. May-10-1990) for display, matching every picker's
 * dateInputFormat. Spelling the month out removes the last ambiguity a
 * numeric format leaves: 05-10 reads as May 10 in the USA and 5 October
 * almost everywhere else.
 */
export function toDisplayDate(value: any): string {
  const d = parseDateOnly(value);
  if (!d) {
    return '';
  }
  return `${MONTHS_SHORT[d.getMonth()]}-${pad2(d.getDate())}-${d.getFullYear()}`;
}

/**
 * 'MMM YYYY' (e.g. May 2025) for month-only values such as the invoice month.
 * Region-independent, and never rolls back to the previous month the way
 * `| date` on a UTC-parsed 'YYYY-MM-01' string does west of UTC.
 */
export function toDisplayMonthYear(value: any): string {
  const d = parseDateOnly(value);
  if (!d) {
    return '';
  }
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * 'MM-DD-YYYY'. The format several existing API payloads are built in; kept
 * separate from `toDisplayDate` so changing what the UI shows can never change
 * what the server receives.
 */
export function toLegacyUsDate(value: any): string {
  const d = parseDateOnly(value);
  if (!d) {
    return '';
  }
  return `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()}`;
}

/** True when both values fall on the same calendar day. */
export function isSameDateOnly(a: any, b: any): boolean {
  const da = parseDateOnly(a);
  const db = parseDateOnly(b);
  return !!da && !!db && da.getTime() === db.getTime();
}

/**
 * Every date-only key the portal binds to a datepicker. Anything listed here is
 * converted to a local-midnight Date before it is patched into a form.
 */
export const DATE_ONLY_FIELDS = [
  'dob',
  'dateOfBirth',
  'dateOfMarriage',
  'marriageDate',
  'graduationDate',
  'effectiveDate',
  'enrollmentDate',
  'dateOfRetirement',
  'lastDayOfGroupBenefits',
  'dateOfHire',
  'terminationDate',
];

/**
 * Returns a shallow copy of `obj` with every date-only field converted to a
 * Date at local midnight, so `bsDatepicker` shows the stored day rather than
 * re-parsing the API string as UTC. Use it on every `patchValue` that is fed
 * from API data.
 */
export function normalizeDateFields<T extends Record<string, any>>(
  obj: T,
  fields: string[] = DATE_ONLY_FIELDS
): T {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  const out: any = { ...obj };
  fields.forEach(key => {
    if (out[key] !== undefined && out[key] !== null && out[key] !== '') {
      out[key] = parseDateOnly(out[key]) ?? out[key];
    }
  });
  return out;
}

/**
 * Turns whatever someone typed into a datepicker into the 'MMM-DD-YYYY' text
 * the picker itself can parse, or null when the text is not yet a complete,
 * unambiguous date.
 *
 * ngx-bootstrap hands typed text to chronos with the configured mask, and a
 * 'MMM' mask cannot read '12122000' - it yields a nonsense date rather than an
 * error. Normalising the input's text first means the picker only ever parses
 * the one format it is configured for.
 */
export function normalizeTypedDate(raw: string): string | null {
  const text = (raw ?? '').trim();
  if (!text) {
    return null;
  }
  // Only act on shapes that are already complete; anything else is still
  // mid-typing and must be left alone.
  const isComplete = DIGITS_ONLY.test(text)
    || US_DATE_ONLY.test(text)
    || ISO_DATE_ONLY.test(text)
    || MMM_DATE_ONLY.test(text)
    || DATE_MMM_ONLY.test(text);
  if (!isComplete) {
    return null;
  }
  const parsed = parseDateOnly(text);
  return parsed ? toDisplayDate(parsed) : null;
}

/**
 * True when the text looks like a finished date but names no real day
 * (13-13-2000, 02312000). Such input must be rejected rather than handed to the
 * picker, which would silently turn it into some unrelated valid date.
 */
export function isUnparseableCompleteDate(raw: string): boolean {
  const text = (raw ?? '').trim();
  if (!text) {
    return false;
  }
  const looksComplete = DIGITS_ONLY.test(text) || US_DATE_ONLY.test(text) || ISO_DATE_ONLY.test(text);
  return looksComplete && parseDateOnly(text) === null;
}

/**
 * Parses a real instant (a login time, an audit timestamp - something that
 * happened at a moment in time, not a calendar day). Unlike `parseDateOnly`
 * this deliberately KEEPS the time and converts it to the viewer's own clock,
 * which is the correct behaviour for an instant.
 *
 * Accepts ISO with or without a zone, 'YYYY-MM-DD HH:mm:ss', and epoch numbers.
 */
export function parseTimestamp(value: any): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }
  // No time part at all: it is a calendar day, so read it as one rather than
  // as UTC midnight (which would show the previous evening west of UTC).
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(raw)) {
    return parseDateOnly(raw);
  }
  // 'YYYY-MM-DD HH:mm:ss' - give it the 'T' so every engine reads it the same
  // way (as local time, which is what the server means by it).
  const spaced = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)(Z|[+-]\d{2}:?\d{2})?$/.exec(raw);
  const candidate = spaced ? `${spaced[1]}T${spaced[2]}${spaced[3] ?? ''}` : raw;

  const d = new Date(candidate);
  if (!isNaN(d.getTime())) {
    return d;
  }
  // Last resort: a date-only value with no time at all.
  return parseDateOnly(raw);
}

/**
 * 'MMM-DD-YYYY h:mm AM/PM' in the viewer's own timezone, e.g. Sep-04-2026 3:07 PM.
 * Month names are the fixed English table, so the text does not change shape
 * with the viewer's region the way `toLocaleString()` does.
 */
export function toDisplayDateTime(value: any): string {
  const d = parseTimestamp(value);
  if (!d) {
    return '';
  }
  const hours24 = d.getHours();
  const suffix = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${MONTHS_SHORT[d.getMonth()]}-${pad2(d.getDate())}-${d.getFullYear()} ${hours12}:${pad2(d.getMinutes())} ${suffix}`;
}
